import { useState } from 'react';
import toast from 'react-hot-toast';
import { MdWarning, MdLightbulbOutline, MdDownload, MdClose } from 'react-icons/md';
import { useStudentFinance } from '../../../hooks/useStudentFinance';
import useClubVocab from '../../../hooks/useClubVocab';
import { useCalendar } from '../../../context/CalendarContext';
import useAuth from '../../../hooks/useAuth';
import { getReceipt, removeDiscount } from '../../../services/feeService';
import { downloadReceiptPdf } from '../../../pages/admin/Payments';
import { FEE_STATUS_BADGE, FEE_STATUS_LABEL } from '../../../utils/feeStatus';

const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

function relativeTime(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'just now';
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days >= 1) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(diff / (60 * 60 * 1000));
  if (hours >= 1) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  return 'just now';
}

const STATUS_PILL = Object.fromEntries(
  Object.keys(FEE_STATUS_BADGE).map(k => [k, { label: FEE_STATUS_LABEL[k], cls: FEE_STATUS_BADGE[k] }])
);

function StatCard({ label, value, sub, valueColor, tint }) {
  return (
    <div className={`rounded-xl border p-3 ${tint || 'bg-gray-50 border-gray-200'}`}>
      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`text-base font-bold mt-1 ${valueColor || 'text-secondary'}`}>{value}</p>
      <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>
    </div>
  );
}

function daysLateDisplay(n) {
  if (n === null || n === undefined || n === 0) return { text: '—', cls: 'text-gray-400' };
  const cls = n > 30 ? 'text-red-600' : n >= 15 ? 'text-amber-600' : 'text-gray-500';
  return { text: `${n} days`, cls };
}

export default function StudentFinanceTab({ studentId }) {
  const { data, isLoading, isError, refetch } = useStudentFinance(studentId);
  const v = useClubVocab();
  const { formatMonth } = useCalendar();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // discount removal is admin-only (no trainers)
  const [dlId, setDlId] = useState(null);
  const [rmId, setRmId] = useState(null); // fee id whose discount is being removed

  // Remove a per-month discount (unpaid months only) — enforced admin-only on the backend.
  const handleRemoveDiscount = async (feePaymentId) => {
    if (!feePaymentId) return;
    setRmId(feePaymentId);
    try {
      await removeDiscount(feePaymentId);
      toast.success('Discount removed');
      refetch();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not remove discount');
    } finally { setRmId(null); }
  };

  // Fetch the full receipt for a paid fee, then hand it to the SAME PDF generator the
  // admin Payments page uses.
  const handleDownloadReceipt = async (feePaymentId) => {
    if (!feePaymentId) return;
    setDlId(feePaymentId);
    try {
      const res = await getReceipt(feePaymentId);
      await downloadReceiptPdf(res.data.receipt, formatMonth);
    } catch { /* receipt unavailable — ignore */ }
    finally { setDlId(null); }
  };

  if (isLoading) {
    return (
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
        <div className="h-32 bg-gray-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  if (isError || !data?.data) {
    return (
      <div className="p-8 text-center">
        <MdWarning className="mx-auto text-3xl text-amber-500 mb-2" />
        <p className="text-sm font-semibold text-secondary mb-3">Couldn't load finance data</p>
        <button onClick={() => refetch()}
          className="px-4 py-2 rounded-xl bg-primary text-white text-xs font-semibold hover:bg-primary/90 transition-colors">
          Try Again
        </button>
      </div>
    );
  }

  const fin = data.data;

  // Empty: never billed.
  if (fin.lifetimeBilled === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm font-semibold text-secondary">No fee records yet.</p>
        <p className="text-xs text-gray-400 mt-1">This {v.member.toLowerCase()} hasn't been billed.</p>
      </div>
    );
  }

  const rate = fin.collectionRate;
  const rateColor = rate >= 85 ? 'text-green-600' : rate >= 60 ? 'text-amber-600' : 'text-red-600';

  // Behavior summary sentiment → left-border tint.
  const avg = fin.averagePaymentDelay;
  const concerning = fin.currentlyOutstanding > 0 || (avg !== null && avg > 15);
  const positive   = fin.currentlyOutstanding === 0 && avg !== null && avg <= 5;
  const summaryBorder = concerning ? 'border-l-amber-400' : positive ? 'border-l-green-400' : 'border-l-gray-300';

  return (
    <div className="p-5 space-y-4">
      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="Lifetime Paid" value={fmtNPR(fin.lifetimePaid)} sub="Total paid since joining"
          tint="bg-green-50 border-green-200" valueColor="text-green-700" />
        <StatCard label="Lifetime Billed" value={fmtNPR(fin.lifetimeBilled)} sub="Across all fees" />
        <StatCard label="Collection Rate" value={`${rate}%`} sub="Paid / billed" valueColor={rateColor} />
        <StatCard label="Unpaid" value={fmtNPR(fin.currentlyOutstanding)} sub="Still owed"
          tint={fin.currentlyOutstanding > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}
          valueColor={fin.currentlyOutstanding > 0 ? 'text-red-700' : 'text-secondary'} />
      </div>

      {/* Behavior strip */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400">
        {avg !== null && (
          <span>Avg delay: <span className="font-semibold text-secondary">{avg} days</span></span>
        )}
        {fin.lastPayment && (
          <>
            <span className="text-gray-300">·</span>
            <span>Last paid: <span className="font-semibold text-secondary">{relativeTime(fin.lastPayment.date)}</span></span>
          </>
        )}
        {fin.paymentStreak > 0 && (
          <>
            <span className="text-gray-300">·</span>
            <span>Streak: <span className="font-semibold text-secondary">{fin.paymentStreak} in a row</span></span>
          </>
        )}
      </div>

      {/* Behavior summary */}
      <div className={`bg-gray-50 border border-gray-100 border-l-4 ${summaryBorder} rounded-xl px-4 py-3 flex items-start gap-2`}>
        <MdLightbulbOutline className="text-gray-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-secondary italic">{fin.behaviorSummary}</p>
      </div>

      {/* Payment history */}
      <div>
        <h3 className="text-sm font-bold text-secondary">Recent Payment History</h3>
        <p className="text-xs text-gray-400 mb-2">Last 12 fee records</p>
        {fin.recentHistory.length === 0 ? (
          <p className="text-xs text-gray-400 py-6 text-center">No records.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-100 rounded-xl">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['Month', 'Status', 'Amount', 'Days Late', 'Paid On'].map(h => (
                    <th key={h} className={`px-3 py-2 font-semibold text-gray-400 uppercase tracking-wide ${h === 'Month' || h === 'Status' ? 'text-left' : 'text-right'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fin.recentHistory.map(r => {
                  const pill = STATUS_PILL[r.status] || { label: r.status, cls: 'bg-gray-100 text-gray-500' };
                  const dl = daysLateDisplay(r.daysLate);
                  return (
                    <tr key={r.feePaymentId} className="border-b border-gray-50 last:border-0">
                      <td className="px-3 py-2 text-secondary whitespace-nowrap">
                        {r.type === 'custom' ? r.description : formatMonth(r.month)}
                        {r.isProrated && r.billableDays ? ` (${r.billableDays} days)` : ''}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full font-semibold ${pill.cls}`}>{pill.label}</span>
                      </td>
                      <td className="px-3 py-2 text-right align-top">
                        <span className="font-medium text-secondary whitespace-nowrap">{fmtNPR(r.amount)}</span>
                        {r.isProrated && (
                          <span className="block text-[10px] font-semibold uppercase tracking-wide text-amber-600 mt-0.5 whitespace-nowrap">Partial</span>
                        )}
                        {r.discount > 0 && (
                          <span className="block text-[10px] font-medium text-emerald-600 mt-0.5 whitespace-nowrap">
                            Discount {fmtNPR(r.discount)}
                            {isAdmin && (r.status === 'pending' || r.status === 'overdue') && (
                              <button
                                type="button"
                                onClick={() => handleRemoveDiscount(r.feePaymentId)}
                                disabled={rmId === r.feePaymentId}
                                title="Remove discount"
                                className="inline-flex items-center align-middle ml-1 text-muted hover:text-red-500 disabled:opacity-40 transition-colors"
                              >
                                <MdClose className="text-xs" />
                              </button>
                            )}
                          </span>
                        )}
                        {r.notes && <span className="block text-[10px] font-normal text-gray-400 mt-0.5">{r.notes}</span>}
                      </td>
                      <td className={`px-3 py-2 text-right whitespace-nowrap ${dl.cls}`}>{dl.text}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <span className="inline-flex items-center gap-2 justify-end text-gray-400">
                          {r.paidOn ? relativeTime(r.paidOn) : '—'}
                          {r.status === 'completed' && (
                            <button
                              type="button"
                              onClick={() => handleDownloadReceipt(r.feePaymentId)}
                              disabled={dlId === r.feePaymentId}
                              title="Download receipt"
                              className="text-gray-400 hover:text-secondary disabled:opacity-40 transition-colors"
                            >
                              <MdDownload className="text-sm" />
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
